import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from '@mantine/hooks';
import classNames from 'classnames';
import styles from './Rescue.module.scss';

/**
 * Long enough to read the longest quote (~55 words) before it moves. The previous 5s outpaced
 * reading and pulled the text away mid-sentence, which on mobile there is no way to stop: the
 * pause handlers below are hover/focus only, so a touch reader cannot hold a quote in place.
 */
const ADVANCE_INTERVAL_MS = 12000;

/**
 * Quotes come from rescue staff rather than adopters on purpose: staff can speak to what happens
 * across many placements, which is the part an adopter cannot vouch for.
 *
 * The first entry is a real, approved quote and carries a first name. The other two are still
 * PLACEHOLDER COPY - invented, unapproved, and attributed to role and region only, because an
 * invented quote must never be pinned on a named person or organisation. Swap the strings behind
 * those two keys once partner rescues have signed off on their own wording, and give them the same
 * first-name attribution as the first entry when they do.
 */
const TESTIMONIALS = [
  { quoteKey: 'rescue_testimonial_recovery_quote', attributionKey: 'rescue_testimonial_recovery_attribution' },
  { quoteKey: 'rescue_testimonial_pickup_quote', attributionKey: 'rescue_testimonial_pickup_attribution' },
  { quoteKey: 'rescue_testimonial_activity_quote', attributionKey: 'rescue_testimonial_activity_attribution' },
];

export default function RescueTestimonials() {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const autoAdvancing = !prefersReducedMotion && !paused;

  useEffect(() => {
    if (!autoAdvancing) return undefined;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % TESTIMONIALS.length),
      ADVANCE_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [autoAdvancing]);

  const active = TESTIMONIALS[index];

  return (
    <section
      className={styles.testimonials}
      aria-label={t('rescue_testimonials_label')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <h2 className={styles.testimonialsTitle}>{t('rescue_testimonials_title')}</h2>
      {/* announcing every rotation would talk over the rest of the page, so only speak the quote
          when nothing is moving it on its own */}
      <figure className={styles.testimonialTrack} aria-live={autoAdvancing ? 'off' : 'polite'}>
        <blockquote className={styles.testimonialQuote}>{t(active.quoteKey)}</blockquote>
        <figcaption className={styles.testimonialAttribution}>{t(active.attributionKey)}</figcaption>
      </figure>
      <div className={styles.testimonialDots}>
        {TESTIMONIALS.map((testimonial, dotIndex) => (
          <button
            key={testimonial.quoteKey}
            type="button"
            className={classNames(styles.testimonialDot, { [styles.testimonialDotActive]: dotIndex === index })}
            aria-label={t('rescue_testimonial_dot_label', {
              number: dotIndex + 1,
              total: TESTIMONIALS.length,
            })}
            aria-current={dotIndex === index}
            onClick={() => setIndex(dotIndex)}
          />
        ))}
      </div>
    </section>
  );
}
